import React, { useContext, useEffect, useState } from 'react'
import { StoresContext } from '@App/contexts'
import Navigation from '@App/navigation'
import { FetchType } from '@Components/WorkItemV2/fetch-type-enum'
import { observer } from 'mobx-react'
import ViewWork from '@Components/ViewWork'
import './index.scss'
import CatalogWorkHeader from '@Components/ViewWork/Headers/CatalogWorkHeader'
import RegHistoryWorkHeader from '@Components/ViewWork/Headers/RegHistoryWorkHeader'
import ModalPortal from '@Components/Popups/ModalPortal'
import Button from '@Components/Buttons/Button'
import { CLEAR_WORK_DRAFT } from '@Const/work-registration-forms'
import { WorkModel } from '@Entities/work/work'
import { useLocation, useNavigate, useParams } from 'react-router'
import { analyticsSendPage } from '@App/common/utils/analytics-utils'
import { PARAM } from '@App/navigation/param'
import { MatchingToolLocationState } from '@Entities/matching/preselected-match-work'
import { formatName } from '@App/helpers/format-name'

interface ViewWorkProps {
    fetchType: FetchType
}
const ViewWorkInternalScene = observer((props: ViewWorkProps) => {
    const { workStore, rightsholderStore, preloaderStore, userStore } = useContext(StoresContext)
    const [sidebarOpen, setSidebarOpen] = useState(false)
    const navigate = useNavigate()
    const location = useLocation()
    const params = useParams<typeof PARAM>()
    const openSidebar = (work: WorkModel) => {
        setSidebarOpen(true)
        workStore.setWorkDraftToLocal(work)
    }

    const closeSidebar = () => {
        setSidebarOpen(false)
        workStore.setWorkDraftToLocal(CLEAR_WORK_DRAFT)
    }

    const claimOnClick = () => {
        workStore.setRedirectingToClaimingTool(true)
        navigate(Navigation.rightholder.matchClaim.claim(rightsholderStore.currentRightsholderId), {
            state: { propertyId: Number(params.workId) },
        })
    }

    const matchOnClick = (work: WorkModel) => {
        const recordingArtistNames = [
            ...new Set(work.matchedRecordings?.recordings.map((recording) => recording.recordingDisplayArtistName)),
        ].filter((artistName): artistName is string => !!artistName)

        const state: MatchingToolLocationState = {
            matchWork: {
                songCode: work.songCode as string,
                title: work.title,
                iswc: work.iswc,
                totalKnownShares: work.totalKnownShares,
                writerNames: work.writers.map((writer) =>
                    formatName(writer.firstName, writer.lastName, writer.fullName)
                ),
                recordingArtistNames,
                sourcePath: location.pathname,
            },
        }
        navigate(Navigation.rightholder.matchClaim.match(rightsholderStore.currentRightsholderId), { state })
    }

    const canUseMatchingTool = !rightsholderStore.isRestricted || userStore.isInternal

    useEffect(() => {
        window.scrollTo(0, 0)
        preloaderStore.openTransparentPreloader(true)
        analyticsSendPage('view_work_internal')
        const fetchType = props.fetchType ? props.fetchType : FetchType.HFA

        const workId = params.workId
        if (!workId || isNaN(Number(workId))) {
            preloaderStore.openTransparentPreloader(false)
            throw new Error('Unable to parse work ID ' + workId)
        }

        workStore.getWorkById(Number(workId), fetchType).then(() => {
            // If it's not a reg history, load the shares
            if (!workStore.workById?.submissionId) {
                rightsholderStore.loadClaimedShares([Number(workId)]).then()
            }
        })
        preloaderStore.openTransparentPreloader(false)

        return () => {
            workStore.destroyViewWork()
            rightsholderStore.resetClaimedShares()
        }
    }, [])

    const work = workStore.workById
    const myShares = rightsholderStore.rightsholderClaimedShares?.workClaimedShares.find(
        () => true
    )?.rightsholderClaimedShare

    return (
        <>
            <title>View Work</title>

            <div className="view-work-scene">
                {work && (
                    <>
                        {work.submissionId ? (
                            <RegHistoryWorkHeader work={work} />
                        ) : (
                            <CatalogWorkHeader
                                work={work}
                                editOnClick={() => work && openSidebar(work)}
                                claimOnClick={() => claimOnClick()}
                            />
                        )}
                        <ViewWork
                            viewWork={work}
                            myShares={myShares}
                            showMatchedRecordings={!work.submissionId}
                            workType={work.submissionId ? 'history' : 'work'}
                            matchOnClick={canUseMatchingTool && work.songCode ? () => matchOnClick(work) : undefined}
                        />
                        <ModalPortal isOpen={sidebarOpen} onClose={closeSidebar}>
                            <div className="warning-edit-work-popup">
                                <h3>Confirmation</h3>
                                <p>
                                    Please confirm that you wish to begin editing &quot;{work.title}
                                    &quot;.
                                </p>
                                <div className="links-block">
                                    <Button mainColor type="secondary" onClick={closeSidebar}>
                                        Cancel
                                    </Button>
                                    <Button
                                        mainColor
                                        type="primary"
                                        typeLink
                                        linkTo={Navigation.rightholder.catalog.workEdit.root(
                                            rightsholderStore.currentRightsholderId,
                                            'work',
                                            work.id as number
                                        )}
                                    >
                                        Edit
                                    </Button>
                                </div>
                            </div>
                        </ModalPortal>
                    </>
                )}
            </div>
        </>
    )
})

export default ViewWorkInternalScene
